import { peek, preload } from 'suspend-react';
import type { Material, Object3D } from 'three';
import { DRACOLoader, type GLTF, GLTFLoader, MeshoptDecoder } from 'three-stdlib';

/** Same decoder path as @react-three/drei useGLTF defaults. */
const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.5/';

let dracoLoader: DRACOLoader | null = null;

/** Mirrors @react-three/drei useGLTF default loader extensions (draco + meshopt on). */
function configureUseGltfLoader(loader: GLTFLoader): void {
  if (!dracoLoader) {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  }
  loader.setDRACOLoader(dracoLoader);
  loader.setMeshoptDecoder(typeof MeshoptDecoder === 'function' ? MeshoptDecoder() : MeshoptDecoder);
}

/** Same post-process as @react-three/fiber useLoader for GLTF results. */
function augmentGltf(gltf: GLTF): GLTF {
  if (gltf.scene) {
    Object.assign(gltf, buildGraph(gltf.scene));
  }
  return gltf;
}

function buildGraph(object: Object3D): { nodes: Record<string, Object3D>; materials: Record<string, Material> } {
  const data: { nodes: Record<string, Object3D>; materials: Record<string, Material> } = {
    nodes: {},
    materials: {},
  };
  object.traverse((obj) => {
    if (obj.name) data.nodes[obj.name] = obj;
    const material = (obj as { material?: Material | Material[] }).material;
    if (material && !Array.isArray(material) && !data.materials[material.name]) {
      data.materials[material.name] = material;
    }
  });
  return data;
}

/**
 * Load a GLB/GLTF once and seed the suspend-react cache that `useGLTF(url)` reads,
 * so view-mode transitions do not trigger a second network + parse pass.
 */
export async function seedUseGltfCache(url: string): Promise<void> {
  const cacheKey = [GLTFLoader, url];
  if (peek(cacheKey) !== undefined) return;

  const loader = new GLTFLoader();
  configureUseGltfLoader(loader);
  const gltf = augmentGltf(await loader.loadAsync(url));

  // useLoader stores `Promise.all` results; a single URL resolves to a one-element array.
  preload(Promise.resolve([gltf]), cacheKey);
}
